<template>
    <div
      :class="this.inline ? 'owal form inline field' : 'owal form field'"
      >
      <label
        v-if="label"
        class="owal form field label"
        >
        {{ label }}
      </label>
      <!-- NOTE: We don't bind to value, because HTML5 doesn't allow setting this directly, except to clear. -->
      <div
        v-if="showDelete"
        :class="`owal-file-input__clear${confirming ? '_confirm' : ''}`"
        >
        <transition>
          <div
            v-if="!confirming"
            class="owal-file-input__clear-begin"
            @click="confirming = true"
            >
            x
          </div>
        </transition>
        <transition>
          <div
            v-if="confirming"
            class="owal-file-input__clear-finish"
            @click="removeFile"
            >
            Confirm
          </div>
        </transition>
      </div>
      <div>
        <div
          :class="`${plain ? '' : `owal-file-input__container${displayImage ? '' : '_empty'}`}`"
          @click="showFilePrompt"
          >
          <input
            ref="fileInput"
            type="file"
            :required="this.required"
            :placeholder="this.placeholder"
            :name="this.name"
            :accept="this.fileTypes"
            style="display:none;"
            @change="setValue($event.target.files)"
            id="files"
            >
          <div
            v-if="plain"
            style="display: flex; flex-direction: row;"
            >
            <owal-button> Upload Image </owal-button>
            <label for="files" class="btn" v-if="showLabel"> {{ fileName }} </label>
          </div>
          <div
            v-else-if="!displayImage"
            class="owal-file-input__banner"
            >
            Click to choose a photo
          </div>
          <div
            v-else
            class="owal-file-input__image-container"
            >
            <img
              slot="content"
              :src="`${displayImage}`"
              :class="imageClass"
              >
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { Input } from './mixins'
import OwalButton from '@/libraries/owal-ui/buttons/OwalButton'
const defaultValue = 'https://s3.amazonaws.com/owal-clips/faces/thumbs/b97e21af-42ee-4433-8de9-199febf370ab.jpg'

export default {
  name: 'OwalFileInput',
  mixins: [Input],
  props: {
    /**
     * @prop fileTypes An array of mime types that the input accepts.
     * @description for example, ['image/*'] or 'text/json'
     */
    fileTypes: {
      type: [Array, String],
      default: () => ['*'],
    },
    /**
     * @prop display Slot instead of default placeholder.
     * @description if you want.'
     */
    plain: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    OwalButton,
  },
  data () {
    return {
      /*
      The value is a JSON object including two keys: `uuid`, and `data_b64`.
      The `uuid` value is used for supporting delete operations.
      */
      value: this.initialValue || { },
      fileReader: undefined, // for input[type=file], we need to have a handle on the file reader...
      confirming: false,
      defaultValue,
      fileName: '',
    }
  },
  computed: {
    displayImage () {
      let image = this.value.data_b64
      if (image && image.startsWith && image.startsWith('http')) {
        const url = new URL(image)
        url.searchParams.append('cacheBust', new Date().valueOf())
        image = url.toString()
      }
      return image
    },
    imageClass () {
      return `owal-file-input__image-preview${this.valueIsDefault ? '_default' : ''}`
    },
    valueIsDefault () {
      return this.value && this.value.data_b64 && this.value.data_b64.startsWith && this.value.data_b64.startsWith(this.defaultValue)
    },
    showDelete () {
      return this.value.data_b64 && this.value.data_b64 !== this.defaultValue && !this.plain
    },
  },
  created () {
    this.applyInitialValue()
  },
  methods: {
    showFilePrompt: function () {
      this.confirming = false
      if (this.fileReader) {
        return
      }
      const fileInput = this.$refs.fileInput
      fileInput.click()
    },
    handleFileLoad (event, blob) {
      this.fileReader = undefined
      let output = event.target.result
      // this.value = output // This will update the UI too.
      this.value = { ...this.value, data_b64: output, blob }
      this.emitChange(this.value)
    },
    setValue (value) {
      if (value && value.startsWith && value.startsWith('http')) {
        // if value is a URL (i.e. defaultValue) create a fake event
        const event = {
          target: {
            result: value,
          },
        }
        this.handleFileLoad(event)
      }
      const isFile = (value.length === 1 && value[0] instanceof Blob)

      if (isFile) {
        const file = value[0]
        const reader = new FileReader()

        reader.readAsDataURL(file)
        /* Reference reader, since reading files is asynchronous. */
        this.fileReader = reader
        this.fileName = value[0].name
        reader.addEventListener('load', e => this.handleFileLoad(e, file))
      }
    },
    removeFile () {
      this.setValue(this.defaultValue)
      this.fileReader = undefined
      this.confirming = false
      this.$refs.fileInput.value = ''
    },
  },
}
</script>

<style scoped>
.owal {
  font-family: 'Lato', 'Avenir';
}
.owal.form.inline.field {
  display: inline-flex;
  flex-direction: column;
  padding: 0.3975rem;
  margin: 0.3975rem;
  box-sizing:border-box;
}

.owal.form.field.label {
  margin-left: 1rem;
  margin-bottom: 0.53rem;
  font-weight: normal;
  font-size: 14px;
  color: #475669;
  letter-spacing: 0;
}

.owal-file-input__container,
.owal-file-input__container_empty {
  background: #F3F4F5;
  border: 1px solid #979797;
  border-radius: 0.571rem;
  height: 10.5rem;
  width: 10.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  background-size: 4.25rem 4.25rem;
  background-repeat: no-repeat;
  background-position-x: center;
  background-position-y: 2.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.owal-file-input__container_empty {
  background-image: url('../assets/img/watermark@3x.png');
}

.owal-file-input__container input,
.owal-file-input__container_empty input {
  visibility: hidden;
}

.owal-file-input__image-container {
  height: 100%;
  width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  align-self: center;
  padding: 0.25rem;
  border-radius: 0.571rem;
}

.owal-file-input__image-preview,
.owal-file-input__image-preview_default {
  max-width: 10rem;
  max-height: 10rem;
  margin-top: 1px;
  align-self: center;
 /* border-radius: 0.571rem; */
 transition: transform 300ms ease;
}

.owal-file-input__image-preview {
  transform: scale(1);
}

.owal-file-input__image-preview_default {
  transform: scale(0.5);
}

.owal-file-input__container .owal-file-input__banner {
  font-size: 1px;
  opacity: 0;
  transform: translateY(-2rem);
}

.owal-file-input__container_empty .owal-file-input__banner {
  font-size: 12px;
  opacity: 0.5;
}

.owal-file-input__container .owal-file-input__banner,
.owal-file-input__container_empty .owal-file-input__banner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.75rem;
  width: 100%;
  flex-grow: 0;
  flex-shrink: 0;
  color: #8492A6;
  transition: all 0.3s ease;
}

.owal-file-input__container:hover,
.owal-file-input__container_empty:hover {
  background-size: 5rem 5rem;
  background-position-y: 2.25rem;
}

.owal-file-input__container_empty:hover .owal-file-input__banner {
  opacity: 1;
}

.owal-file-input__clear,
.owal-file-input__clear_confirm {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  height: 1.3rem;
  flex-direction: row-reverse;
  z-index: 10;
  height: fit-content;
  width: fit-content;
  position: relative;
  cursor: pointer;
  margin-bottom: -0.65rem;
  margin-top: -0.65rem;
  margin-left: -0.65rem;
  background-color: #000;
  color: #fff;
  border: 2px solid rgba(0, 0, 0, 1);
  transition: border 300ms linear;
  padding: 0.125rem 0.5rem;
  border-radius:  0.9rem;
}

.owal-file-input__clear:hover,
.owal-file-input__clear_confirm:hover {
  border: 2px solid rgba(255, 255, 255, 1);
  box-shadow: #000 0 0 2px;
}

.owal-file-input__clear .owal-file-input__clear-begin,
.owal-file-input__clear_confirm .owal-file-input__clear-finish {
  height: 1.3rem;
  line-height: 1.3rem;
  transition: all 300ms ease;
}

.owal-file-input__clear .owal-file-input__clear-begin {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  width: 1.3rem;
  height: 100%;
  line-height: 100%;
  padding-bottom: 0.20rem; /* baseline adjust */
  font-weight: bold;
}

.v-on-enter {
  animation: slide 300ms ease 0;
}

@keyframes slide {
  from {
    color: rgba(255, 255, 255, 0);
    width: 0;
    opacity: 0;
  }
  to {
    width: fit-content;
    opacity: 0;
    color: rgba(255, 255, 255, 1);
  }
}

</style>
